"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DepartmentService, {
  Department,
  DepartmentStatusCode,
} from "../services/department-service";
import DepartmentTable, { DepartmentRow } from "../components/department-table";
import DepartmentCreateModal, { DepartmentFormValues } from "../components/department-create-modal";
import DepartmentDetailModal from "../components/department-detail-modal";
import DepartmentStatusConfirmModal from "../components/department-status-confirm-modal";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import ActiveDepartmentTab from "./active-department-tab";
import InactiveDepartmentTab from "./inactive-department-tab";
import { ATTENDANCE_MODE } from "../../../constants/status";
import { removeVietnameseFromString } from "../../../utils/helpers";

/** Bỏ dấu + lowercase để tìm kiếm không dấu (gõ "tai xe" ra "Tài xế"). */
const normalize = (v: string) => removeVietnameseFromString(v || "").toLowerCase();

const DepartmentIndexPage: React.FC = () => {
  const [rows, setRows] = useState<DepartmentRow[]>([]);
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [search, setSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [editRow, setEditRow] = useState<DepartmentRow | null>(null);
  const [detailRow, setDetailRow] = useState<DepartmentRow | null>(null);
  const [detailData, setDetailData] = useState<Department | undefined>();
  const [pendingStatus, setPendingStatus] = useState<{ row: DepartmentRow; target: "active" | "inactive" } | null>(null);

  // services
  const { fetch: fetchList, loading: loadingList } = DepartmentService.list();
  const { fetch: createDepartment, loading: creating } = DepartmentService.create();
  const { fetch: updateDepartment, loading: updating } = DepartmentService.update();
  const { fetch: changeStatusDepartment, loading: changingStatus } = DepartmentService.changeStatus();
  const { fetch: fetchDetail, loading: loadingDetail } = DepartmentService.detail();


  const listRef = useRef(fetchList);
  useEffect(() => { listRef.current = fetchList; }, [fetchList]);

  const load = useCallback(async () => {
    const res = await listRef.current();
    if (res && isSuccess(res)) {
      const apiData = Array.isArray(res.data) ? res.data : [];
      setRows(
        apiData.map((d: any) => ({
          id: d.id,
          name: d.name,
          // Đếm từ danh sách nhân sự BE trả kèm; cột `user_count` chỉ dùng khi thiếu `users`
          // (bộ đếm này tăng/giảm khi chuyển bộ phận nên có thể lệch với thực tế).
          user_count: Array.isArray(d.users) ? d.users.length : Number(d.user_count ?? 0),
          description: d.description ?? "",
          statusCode: Number(d.status) as DepartmentStatusCode,
          parent_department_id: Number(d.parent_department_id ?? 0),
          created_at: d.created_at,
          updated_at: d.updated_at,
          default_attendance_mode: Number(d.default_attendance_mode ?? ATTENDANCE_MODE.SHIFT) as ATTENDANCE_MODE,
        }))
      );
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // BE trả toàn bộ danh sách (không phân trang) nên lọc ngay trên client theo tên / mô tả.
  const filteredRows = useMemo(() => {
    const q = normalize(search.trim());
    if (!q) return rows;
    return rows.filter((r) => normalize(r.name).includes(q) || normalize(r.description).includes(q));
  }, [rows, search]);

  const activeRows = useMemo(
    () => filteredRows.filter((r) => r.statusCode === DepartmentStatusCode.ACTIVE),
    [filteredRows]
  );
  const inactiveRows = useMemo(
    () => filteredRows.filter((r) => r.statusCode === DepartmentStatusCode.INACTIVE),
    [filteredRows]
  );

  const onCreate = async (v: DepartmentFormValues): Promise<boolean> => {
    const payload = {
      company_id: 1, // tuỳ logic của bạn
      parent_department_id: v.parent_department_id ?? 0,
      name: v.name.trim(),
      description: v.description?.trim() ?? "",
      status: v.isActive ? DepartmentStatusCode.ACTIVE : DepartmentStatusCode.INACTIVE,
      default_attendance_mode: v.default_attendance_mode,
    };
    const res = await createDepartment(payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Tạo bộ phận thành công", type: "success" });
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể tạo bộ phận", type: "error" });
    return false;
  };

  const onEditSubmit = async (v: DepartmentFormValues): Promise<boolean> => {
    if (!editRow) return false;
    const payload = {
      parent_department_id: v.parent_department_id ?? 0,
      name: v.name.trim(),
      description: v.description?.trim() ?? "",
      status: v.isActive ? DepartmentStatusCode.ACTIVE : DepartmentStatusCode.INACTIVE,
      default_attendance_mode: v.default_attendance_mode,
    };
    const res = await updateDepartment(editRow.id, payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Cập nhật bộ phận thành công", type: "success" });
      setEditRow(null);
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật", type: "error" });
    return false;
  };

  const handleViewDetail = async (row: DepartmentRow) => {
    setDetailRow(row);
    setDetailData(undefined);
    const res = await fetchDetail(row.id);
    if (res && isSuccess(res)) setDetailData(res.data);
    else setDetailRow(null);
  };

  const handleCloseDetailModal = () => {
    setDetailRow(null);
    setDetailData(undefined);
  };

  const onRequestDeactivate = (row: DepartmentRow) => setPendingStatus({ row, target: "inactive" });
  const onRequestActivate = (row: DepartmentRow) => setPendingStatus({ row, target: "active" });

  const onConfirmChangeStatus = async () => {
    if (!pendingStatus) return;
    const next = pendingStatus.target === "active" ? DepartmentStatusCode.ACTIVE : DepartmentStatusCode.INACTIVE;
    const res = await changeStatusDepartment(pendingStatus.row.id, { status: next });
    if (res && isSuccess(res)) {
      showToast({
        message: pendingStatus.target === "active" ? "Đã kích hoạt bộ phận" : "Đã ngưng bộ phận",
        type: "success",
      });
      setPendingStatus(null);
      void load();
      return;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật trạng thái", type: "error" });
    setPendingStatus(null);
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">Danh sách bộ phận</h1>
        <button
          onClick={() => setOpenCreate(true)}
          className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>THÊM BỘ PHẬN</span>
        </button>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-lg bg-slate-50 px-6 py-3">
        <div className="flex flex-wrap gap-6">
          <button
            onClick={() => setTab("active")}
            className={`relative pb-2 text-sm font-semibold uppercase tracking-wide ${tab === "active" ? "border-b-2 border-green-500 text-green-600" : "text-slate-400 hover:text-slate-600"
              }`}
          >
            ĐANG HOẠT ĐỘNG
            <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-green-600">{activeRows.length}</span>
          </button>
          <button
            onClick={() => setTab("inactive")}
            className={`relative pb-2 text-sm font-semibold uppercase tracking-wide ${tab === "inactive" ? "border-b-2 border-green-500 text-green-600" : "text-slate-400 hover:text-slate-600"
              }`}
          >
            TẠM NGƯNG
            <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-slate-600">{inactiveRows.length}</span>
          </button>
        </div>

        <div className="relative w-full sm:w-[300px]">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm kiếm bộ phận..."
            className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all"
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-2 overflow-x-auto">
        {tab === "active" ? (
          <ActiveDepartmentTab
            rows={filteredRows}
            isLoading={loadingList}
            onEdit={setEditRow}
            onDeactivate={onRequestDeactivate}
            onViewDetail={handleViewDetail}
          />
        ) : (
          <InactiveDepartmentTab
            rows={filteredRows}
            isLoading={loadingList}
            onEdit={setEditRow}
            onActivate={onRequestActivate}
            onViewDetail={handleViewDetail}
          />
        )}
      </div>

      {/* Modals */}
      {openCreate && (
        <DepartmentCreateModal
          onClose={() => setOpenCreate(false)}
          onSubmit={onCreate}
          isSubmitting={creating}
        />
      )}

      {editRow && (
        <DepartmentCreateModal
          onClose={() => setEditRow(null)}
          onSubmit={onEditSubmit}
          isSubmitting={updating}
          titleText="Cập nhật bộ phận"
          submitText="Cập nhật"
          initialValues={{
            name: editRow.name,
            parent_department_id: editRow.parent_department_id ?? 0,
            description: editRow.description ?? "",
            isActive: editRow.statusCode === DepartmentStatusCode.ACTIVE,
            default_attendance_mode: editRow.default_attendance_mode ?? ATTENDANCE_MODE.SHIFT,
          }}
        />
      )}

      {pendingStatus && (
        <DepartmentStatusConfirmModal
          mode={pendingStatus.target === "inactive" ? "deactivate" : "activate"}
          departmentName={pendingStatus.row.name}
          onCancel={() => setPendingStatus(null)}
          onConfirm={onConfirmChangeStatus}
          isProcessing={changingStatus}
        />
      )}

      {detailRow && detailData && (
        <DepartmentDetailModal
          name={detailRow.name}
          data={detailData}
          loading={loadingDetail}
          onClose={handleCloseDetailModal}
        />
      )}
    </div>
  );
};

export default DepartmentIndexPage;
